import { useState, useRef } from 'react'
import FlowShell from './FlowShell'
import { usePlans } from '../../hooks/useBilling'
import { formatNaira } from '../../utils/formatters'

// ─── helpers ──────────────────────────────────────────────────────────────────

function passwordStrength(pw) {
  if (!pw) return null
  const long    = pw.length >= 12
  const medium  = pw.length >= 8
  const upper   = /[A-Z]/.test(pw)
  const digit   = /[0-9]/.test(pw)
  const special = /[^A-Za-z0-9]/.test(pw)
  if (long && upper && digit && special) return 'strong'
  if (medium && upper && digit)          return 'medium'
  if (medium)                            return 'weak'
  return 'too-short'
}

const STRENGTH_LABEL = { 'too-short': 'Too short', weak: 'Weak', medium: 'Fair', strong: 'Strong' }
const STRENGTH_COLOR = {
  'too-short': 'bg-red-500',
  weak:        'bg-red-500',
  medium:      'bg-amber-400',
  strong:      'bg-green-500',
}
const STRENGTH_BARS  = { 'too-short': 1, weak: 1, medium: 2, strong: 3 }

const BUSINESS_TYPES = ['Independent Station', 'Fuel Distributor', 'Fleet Operator', 'Other']
const COMPANY_SIZES  = ['1–10 employees', '11–50 employees', '51–200 employees', '200+ employees']

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: '1–2 stations, core daily operations',
    badge: null,
  },
  {
    id: 'growth',
    name: 'Growth',
    tagline: '3–10 stations, advanced reporting & credit sales',
    badge: 'Most popular',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: '10+ stations, unlimited branches & users',
    badge: null,
  },
]


// ─── shared styled inputs ─────────────────────────────────────────────────────

function Field({ label, error, children }) {
  return (
    <div>
      <label className="block text-sm text-white/50 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
    </div>
  )
}

const inputCls =
  'w-full bg-transparent border-b border-white/20 focus:border-green-500 py-3 text-lg text-white placeholder:text-white/30 outline-none transition-colors'

function TextInput({ label, error, ...props }) {
  return (
    <Field label={label} error={error}>
      <input className={inputCls} {...props} />
    </Field>
  )
}

function SelectInput({ label, error, options, placeholder, ...props }) {
  return (
    <Field label={label} error={error}>
      <select
        className="w-full bg-transparent border-b border-white/20 focus:border-green-500 py-3 text-lg text-white outline-none transition-colors appearance-none"
        {...props}
      >
        <option value="" disabled className="bg-[#0F0F0F]">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o} className="bg-[#1a1a1a]">{o}</option>
        ))}
      </select>
    </Field>
  )
}

function PrimaryBtn({ children, loading, disabled, onClick, type = 'button' }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className="w-full py-4 rounded-xl bg-green-500 text-black font-semibold text-base
                 disabled:opacity-40 disabled:cursor-not-allowed
                 active:scale-95 transition-transform duration-100"
    >
      {loading
        ? <span className="flex items-center justify-center gap-2">
            <Spinner /><span>Please wait…</span>
          </span>
        : children}
    </button>
  )
}

function Spinner() {
  return (
    <svg className="animate-spin w-5 h-5 text-black" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor"
        d="M4 12a8 8 0 018-8v4l3-3-3-3v4A8 8 0 104 12z" />
    </svg>
  )
}

// ─── Step 0: Personal details ─────────────────────────────────────────────────

function PersonalStep({ form, onChange, onNext, presetPlanName }) {
  const [touched, setTouched] = useState({})
  const [showPw, setShowPw] = useState(false)

  function touch(field) { setTouched((t) => ({ ...t, [field]: true })) }

  function validate() {
    const e = {}
    if (!form.fullName.trim())    e.fullName = 'Full name is required.'
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = 'Valid email is required.'
    if (!form.phone.trim())       e.phone = 'Phone / WhatsApp number is required.'
    const st = passwordStrength(form.password)
    if (!st || st === 'too-short') e.password = 'Password must be at least 8 characters.'
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match.'
    return e
  }

  function handleNext() {
    const allFields = { fullName: true, email: true, phone: true, password: true, confirmPassword: true }
    setTouched(allFields)
    const errs = validate()
    if (Object.keys(errs).length === 0) onNext()
  }

  const errs = Object.keys(touched).length > 0 ? validate() : {}
  const st   = passwordStrength(form.password)

  return (
    <>
      <div className="space-y-6 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-white leading-tight">Create your account</h1>
          <p className="text-sm text-white/60 mt-2 leading-relaxed">
            {presetPlanName
              ? `You're subscribing to the ${presetPlanName} plan. Create your account, then complete payment.`
              : 'Start your 14-day free trial. No credit card required.'}
          </p>
          {presetPlanName && (
            <span className="inline-block mt-3 text-xs font-medium text-green-400 bg-green-500/10 border border-green-500/20 px-3 py-1 rounded-full">
              {presetPlanName} plan · pay after signup
            </span>
          )}
        </div>

        <div className="space-y-5">
          <TextInput label="Full name" value={form.fullName} placeholder="Emeka Okonkwo"
            onChange={(e) => onChange('fullName', e.target.value)}
            onBlur={() => touch('fullName')} error={touched.fullName && errs.fullName} />

          <TextInput label="Email address" type="email" value={form.email} placeholder="emeka@example.com"
            onChange={(e) => onChange('email', e.target.value)}
            onBlur={() => touch('email')} error={touched.email && errs.email} />

          <TextInput label="Phone / WhatsApp" type="tel" value={form.phone} placeholder="08012345678"
            onChange={(e) => onChange('phone', e.target.value)}
            onBlur={() => touch('phone')} error={touched.phone && errs.phone} />

          <Field label="Password" error={touched.password && errs.password}>
            <div className="relative">
              <input
                type={showPw ? 'text' : 'password'}
                value={form.password}
                placeholder="8+ characters"
                onChange={(e) => onChange('password', e.target.value)}
                onBlur={() => touch('password')}
                className={inputCls + ' pr-16'}
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-0 bottom-3 text-xs text-white/40 hover:text-white/70"
              >
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
            {form.password && st && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex gap-1">
                  {[1, 2, 3].map((n) => (
                    <div key={n}
                      className={`h-1 w-8 rounded-full transition-colors duration-200 ${
                        n <= STRENGTH_BARS[st] ? STRENGTH_COLOR[st] : 'bg-white/15'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-xs text-white/50">{STRENGTH_LABEL[st]}</span>
              </div>
            )}
          </Field>

          <TextInput label="Confirm password" type="password" value={form.confirmPassword}
            placeholder="Repeat password"
            onChange={(e) => onChange('confirmPassword', e.target.value)}
            onBlur={() => touch('confirmPassword')} error={touched.confirmPassword && errs.confirmPassword} />
        </div>
      </div>

      <PrimaryBtn onClick={handleNext}>Continue →</PrimaryBtn>
    </>
  )
}

// ─── Step 1: Organisation details ─────────────────────────────────────────────

function OrgStep({ form, onChange, onNext, onBack }) {
  const [touched, setTouched] = useState({})

  function touch(field) { setTouched((t) => ({ ...t, [field]: true })) }

  function validate() {
    const e = {}
    if (!form.orgName.trim())    e.orgName = 'Company name is required.'
    if (!form.businessType)      e.businessType = 'Select a business type.'
    const n = parseInt(form.numBranches, 10)
    if (!n || n < 1)             e.numBranches = 'Enter at least 1.'
    if (!form.companySize)       e.companySize = 'Select company size.'
    return e
  }

  function handleNext() {
    const all = { orgName: true, businessType: true, numBranches: true, companySize: true }
    setTouched(all)
    const errs = validate()
    if (Object.keys(errs).length === 0) onNext()
  }

  const errs = Object.keys(touched).length > 0 ? validate() : {}

  return (
    <>
      <div className="space-y-6 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-white leading-tight">Tell us about your business</h1>
          <p className="text-sm text-white/60 mt-2 leading-relaxed">We use this to customise PetroTrack for you.</p>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="text-sm text-white/40 hover:text-white/70 -mb-2 transition-colors"
        >
          ← Back
        </button>

        <div className="space-y-5">
          <TextInput label="Company / organisation name" value={form.orgName} placeholder="Delta Petroleum Ltd"
            onChange={(e) => onChange('orgName', e.target.value)}
            onBlur={() => touch('orgName')} error={touched.orgName && errs.orgName} />

          <SelectInput label="Business type" options={BUSINESS_TYPES} placeholder="Select type…"
            value={form.businessType}
            onChange={(e) => onChange('businessType', e.target.value)}
            onBlur={() => touch('businessType')} error={touched.businessType && errs.businessType} />

          <TextInput label="CAC registration number (optional)" value={form.cacNumber} placeholder="RC 123456"
            onChange={(e) => onChange('cacNumber', e.target.value)} />

          <TextInput label="Number of stations / branches" type="number" min={1} value={form.numBranches}
            onChange={(e) => onChange('numBranches', e.target.value)}
            onBlur={() => touch('numBranches')} error={touched.numBranches && errs.numBranches} />

          <SelectInput label="Company size" options={COMPANY_SIZES} placeholder="Select size…"
            value={form.companySize}
            onChange={(e) => onChange('companySize', e.target.value)}
            onBlur={() => touch('companySize')} error={touched.companySize && errs.companySize} />
        </div>
      </div>

      <PrimaryBtn onClick={handleNext}>Continue →</PrimaryBtn>
    </>
  )
}

// ─── Step 2: Plan selection ────────────────────────────────────────────────────

function PlanStep({ selectedPlan, onChange, onSubmit, onBack, loading, error }) {
  // Monthly price per tier from the live billing catalogue (DB-backed since
  // Phase 4) so the signup flow never drifts from what the customer is charged.
  const billingPlans = usePlans()
  const PRICE_BY_TIER = Object.fromEntries(billingPlans.map((p) => [p.tier, p.amount]))
  return (
    <>
      <div className="space-y-6 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-white leading-tight">Choose a plan</h1>
          <p className="text-sm text-white/60 mt-2 leading-relaxed">All plans include a 14-day free trial. No payment needed today.</p>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="text-sm text-white/40 hover:text-white/70 -mb-2 transition-colors"
        >
          ← Back
        </button>

        <div className="space-y-3 pt-1">
          {PLANS.map((plan) => {
            const selected = selectedPlan === plan.id
            return (
              <button
                key={plan.id}
                type="button"
                onClick={() => onChange(plan.id)}
                className={`
                  w-full text-left py-4 px-5 rounded-xl border transition-all duration-150
                  active:scale-[0.98]
                  ${selected
                    ? 'border-green-500 bg-green-500/10'
                    : 'border-white/10 bg-white/5 hover:border-white/20'
                  }
                `}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white font-semibold text-base">{plan.name}</span>
                  {plan.badge && (
                    <span className="text-xs font-medium text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full">
                      {plan.badge}
                    </span>
                  )}
                </div>
                <p className="text-sm text-white/55 leading-snug">{plan.tagline}</p>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-lg font-bold text-white">{formatNaira(PRICE_BY_TIER[plan.id])}</span>
                  <span className="text-xs text-white/50">/ month</span>
                </div>
                <p className="text-xs mt-1 font-medium text-green-500/80">14-day free trial included</p>
              </button>
            )
          })}
        </div>

        {error && (
          <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
            {error}
          </p>
        )}
      </div>

      <PrimaryBtn
        onClick={onSubmit}
        loading={loading}
        disabled={!selectedPlan}
      >
        Create account
      </PrimaryBtn>
    </>
  )
}

// ─── SignupFlow (root) ─────────────────────────────────────────────────────────

export default function SignupFlow({ onSignUp, signUpError, onGoToLogin, initialPlan, onResendVerification }) {
  const presetPlan = ['starter', 'growth', 'enterprise'].includes(initialPlan) ? initialPlan : ''
  const presetPlanName = PLANS.find((p) => p.id === presetPlan)?.name || ''
  const [step, setStep] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [verifyEmail, setVerifyEmail] = useState(null) // email awaiting confirmation
  const [resendState, setResendState] = useState('')   // '' | 'sending' | 'sent' | error text
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', password: '', confirmPassword: '',
    orgName: '', businessType: '', cacNumber: '', numBranches: 1, companySize: '',
    plan: presetPlan,
  })

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }))
  }

  async function handleSubmit() {
    if (!form.plan) return
    setSubmitting(true)
    const result = await onSignUp(form)
    setSubmitting(false)
    if (result?.needsVerification) setVerifyEmail(result.email)
  }

  async function handleResend() {
    setResendState('sending')
    const result = await onResendVerification?.(verifyEmail)
    setResendState(result?.success === false ? (result.message || 'Could not resend — try again shortly.') : 'sent')
  }

  // Account created — waiting on the email confirmation link.
  if (verifyEmail) {
    return (
      <FlowShell step={3} totalSteps={3}>
        <div className="text-center py-6">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-500/15 flex items-center justify-center">
            <svg className="w-8 h-8 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3.5 7 8.5 6.2L20.5 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Check your email</h2>
          <p className="text-white/60 text-sm leading-relaxed">
            We sent a confirmation link to <span className="text-white font-semibold">{verifyEmail}</span>.
            Click it to activate your account, then sign in. Check spam if you don't see it.
          </p>
          {resendState === 'sent' && (
            <p className="text-green-400 text-sm mt-4">Confirmation email re-sent.</p>
          )}
          {resendState && resendState !== 'sent' && resendState !== 'sending' && (
            <p className="text-red-400 text-sm mt-4">{resendState}</p>
          )}
        </div>
        <div className="space-y-3 mt-4">
          <PrimaryBtn onClick={onGoToLogin}>Go to sign in</PrimaryBtn>
          <button
            onClick={handleResend}
            disabled={resendState === 'sending' || resendState === 'sent'}
            className="w-full py-3 text-sm text-white/50 hover:text-white disabled:opacity-40 transition"
          >
            {resendState === 'sending' ? 'Resending…' : "Didn't get it? Resend email"}
          </button>
        </div>
      </FlowShell>
    )
  }

  return (
    <FlowShell step={step} totalSteps={3}>
      {step === 0 && (
        <PersonalStep
          form={form}
          onChange={update}
          onNext={() => setStep(1)}
          presetPlanName={presetPlanName}
        />
      )}
      {step === 1 && (
        <OrgStep
          form={form}
          onChange={update}
          onNext={() => setStep(2)}
          onBack={() => setStep(0)}
        />
      )}
      {step === 2 && (
        <PlanStep
          selectedPlan={form.plan}
          onChange={(p) => update('plan', p)}
          onSubmit={handleSubmit}
          onBack={() => setStep(1)}
          loading={submitting}
          error={signUpError}
        />
      )}

      {/* Bottom link to login */}
      <div className="text-center pt-2">
        <p className="text-sm text-white/40">
          Already have an account?{' '}
          <button
            type="button"
            onClick={onGoToLogin}
            className="text-green-500 font-medium hover:text-green-400"
          >
            Sign in
          </button>
        </p>
      </div>
    </FlowShell>
  )
}
